import React, { useState, useEffect, useContext } from 'react';
import classes from './OnboardingFestival.module.css';
import EditFestival from '../Forms/EditFestival';
import Loading from '../UI/Loading';
import AuthDrawer from '../Modals/AuthDrawer';
import Alert from '../Modals/Alert';
import { UserContext } from '../../store/user-context';
import { collection, query, where, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../../config';

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M19 12H5M5 12L11 18M5 12L11 6" stroke="#2c3030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="7" stroke="#abacac" strokeWidth="1.8"/>
    <path d="M16.5 16.5L21 21" stroke="#abacac" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const OnboardingFestival = ({ group, onSignIn, onBack, showBack = true }) => {
  const [working, setWorking] = useState(false);
  const user = auth.currentUser;
  const [festivals, setFestivals] = useState([]);
  const [festival, setFestival] = useState(group?.festival);
  const [pendingFestival, setPendingFestival] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAuthDrawer, setShowAuthDrawer] = useState(false);
  const [authMode, setAuthMode] = useState('signup');
  const [duplicateFestival, setDuplicateFestival] = useState(null);
  const UserCtx = useContext(UserContext);
  const groups = UserCtx.groups;
  // showBack is false only for a genuine first-load with no navigation
  // history (see RootNavigator.js) — every other call site (Root.js, for a
  // group mid-onboarding or a draft created via My Events -> Add event)
  // defaults to true, preserving the existing back-navigation logic below.
  const allowBackNavigation = showBack && (groups?.length > 1 ? false : true);

  // An authenticated user who already has another, fully set-up event can
  // bail out of festival selection and return to that event's homepage.
  // NB: 'currentGroupId' already points at this draft group by the time we
  // get here (addNewGroup switches to it), so we rely on a separate key
  // captured right before that switch happened.
  const previousGroupId = localStorage.getItem('lastActiveGroupId');
  const previousGroup = groups?.find(g => g.id === previousGroupId && g.id !== group?.id);
  const canExit = showBack && !!user && !!previousGroup?.festival;

  const handleExit = async () => {
    // Root.js's addNewGroup creates this group the instant "Add event" is
    // tapped (see EventsSheet.js's handleAdd), before any festival has been
    // chosen — this screen only ever renders for a group with no festival
    // yet, so backing out here always means abandoning an empty draft.
    // Without deleting it, it's left behind forever as an "Unnamed event"
    // in My events.
    const draftGroupId = group?.id;
    if (draftGroupId) {
      setWorking(true);
      try {
        await deleteDoc(doc(db, 'groups', draftGroupId));
      } catch (err) {
        console.log('Error deleting abandoned draft group:', err);
      }
      setWorking(false);
    }
    UserCtx.setCurrent(previousGroupId);
  };

  const selectFestival = (selected) => {
    const alreadyAdded = groups?.some(g => g.festival?.id === selected.id);
    if (alreadyAdded) {
      setDuplicateFestival(selected);
    } else {
      setFestival(selected);
    }
  };

  const goBack = () => {
    if (group && group.id) {
      setWorking(true);
      const groupRef = doc(db, 'groups', group.id);
      updateDoc(groupRef, { acknowledgement: false })
        .catch(() => {})
        .finally(() => setWorking(false));
    } else {
      onBack && onBack();
    }
  };

  const saveFestivalToGroup = async () => {
    if (group && group.id) {
      // Logged in user — save to Firebase
      try {
        setWorking(true);
        const groupRef = doc(db, 'groups', group.id);
        await updateDoc(groupRef, { festival: festival });
      } catch (err) {
        alert("There was a problem updating your group.");
      }
      setWorking(false);
    } else {
      // Guest user — store locally and show auth drawer
      localStorage.setItem('selectedFestival', JSON.stringify(festival));
      setPendingFestival(festival);
      setFestival(null);
      setShowAuthDrawer(true);
    }
  };

  useEffect(() => {
    if (!festival) return;
    saveFestivalToGroup();
  }, [festival]);

  useEffect(() => {
    setFestivals([]);
    const q = query(collection(db, 'festivals'), where("active", "==", true));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const festivalsSnapshot = [];
      querySnapshot.forEach((doc) => {
        const dataObj = { ...doc.data() };
        delete dataObj.items;
        festivalsSnapshot.push(dataObj);
      });
      setFestivals(festivalsSnapshot);
    }, (error) => {
      console.log(error.message);
    });
    return () => { unsubscribe(); setFestivals([]); };
  }, []);

  // Nearest-upcoming first (soonest startdate), then already-finished
  // festivals last (most-recently-ended first) — "finished" is judged by
  // enddate (falling back to startdate for a single-day event with no
  // enddate) so a multi-day festival that's currently running doesn't drop
  // to the bottom just because it already started. Festivals missing a
  // date entirely sort to the end of whichever bucket they'd land in.
  const now = new Date();
  const festivalEndDate = (f) => {
    const raw = f.enddate || f.startdate;
    return raw ? new Date(raw) : null;
  };
  const isPastFestival = (f) => {
    const end = festivalEndDate(f);
    return !!end && end < now;
  };

  const filteredFestivals = festivals
    .filter(f => f.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      const aPast = isPastFestival(a);
      const bPast = isPastFestival(b);
      if (aPast !== bPast) return aPast ? 1 : -1;

      const aStart = a.startdate ? new Date(a.startdate) : null;
      const bStart = b.startdate ? new Date(b.startdate) : null;
      if (!aStart && !bStart) return 0;
      if (!aStart) return 1;
      if (!bStart) return -1;
      // Upcoming: soonest first. Past: most recently ended first.
      return aPast ? bStart - aStart : aStart - bStart;
    });

  if (working) return <Loading msg='Loading' />;

  return (
    <div className="app-wrapper">
    <div className={classes.container}>
      {showAuthDrawer && (
        <AuthDrawer
          onCreateAccount={() => { setShowAuthDrawer(false); setAuthMode('signup'); onSignIn && onSignIn('signup'); }}
          onLogin={() => { setShowAuthDrawer(false); setAuthMode('login'); onSignIn && onSignIn('login'); }}
          onClose={() => setShowAuthDrawer(false)}
        />
      )}

      {duplicateFestival && (
        <Alert
          title="You already have this event…"
          message={`You already have ${duplicateFestival.name} in your events. You can still add it again if you'd like a separate copy.`}
          confirmLabel="Add anyway"
          onConfirm={() => { setFestival(duplicateFestival); setDuplicateFestival(null); }}
          onCancel={() => setDuplicateFestival(null)}
        />
      )}

      {/* Top bar */}
      <div className={classes.topBar}>
        {allowBackNavigation && (
          <button className={classes.backBtn} onClick={goBack}>
            <BackIcon />
          </button>
        )}
        {!allowBackNavigation && canExit && (
          <button className={classes.backBtn} onClick={handleExit} aria-label="Back to home">
            <BackIcon />
          </button>
        )}
        {!allowBackNavigation && !canExit && <div className={classes.backPlaceholder} />}
        {onSignIn && (
          <button className={classes.signInBtn} onClick={() => onSignIn('signup')}>
            Sign in / Sign up
          </button>
        )}
      </div>

      {/* Heading */}
      <div className={classes.heading}>
        <h1 className={classes.title}>Where are you going?</h1>
        <p className={classes.subtitle}>
          Select a festival to get started. Or you can create your own Event!
        </p>
        {group?.id && <EditFestival
          groupId={group?.id}
          festival={festival}
          pressHandler={setFestival}
        />}
        {!group?.id && (
          <button
            onClick={() => { onSignIn && onSignIn('signup'); }}
            style={{
              background: 'none', border: 'none', padding: 0, margin: 0,
              fontFamily: "'Poppins', Arial, sans-serif", fontSize: '14px',
              fontWeight: 600, color: '#2c3030', textDecoration: 'underline',
              textUnderlineOffset: '3px', cursor: 'pointer', display: 'inline'
            }}
          >
            + Create my own event
          </button>
        )}
      </div>

      {/* Search */}
      <div className={classes.searchWrapper}>
        <div className={classes.searchBar}>
          <SearchIcon />
          <input
            className={classes.searchInput}
            type="text"
            placeholder="Search for your festival"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            spellCheck="false"
          />
        </div>
      </div>

      {/* Festival grid */}
      <div className={classes.grid}>
        {filteredFestivals.map(festival => (
          <div
            key={festival.id}
            className={classes.festivalCard}
            onClick={() => selectFestival(festival)}
          >
            <div className={classes.festivalImageWrapper}>
              {festival.image?.src ? (
                <img
                  src={festival.image.src}
                  alt={festival.name}
                  className={classes.festivalImage}
                />
              ) : (
                <div className={classes.festivalImageFallback}>
                  <span>{festival.name?.charAt(0)}</span>
                </div>
              )}
            </div>
            <p className={classes.festivalName}>{festival.name}</p>
          </div>
        ))}
      </div>

    </div>
    </div>
  );
};

export default OnboardingFestival;
